'use client'
import React from 'react'
import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';
import Link from 'next/link';
import cn  from 'classnames';
import Image from 'next/image';

interface RichTextRendererProps {
  content?: BlocksContent
  className?: string
}

/**
 * Renders rich text content using customizable block and modifier components.
 *
 * @param content - The rich text content to render, typically in a structured format compatible with BlocksRenderer.
 * @param className - Optional additional CSS class names to apply to rendered elements.
 * @returns A React element rendering the rich text content, or `null` if content is `undefined` or `null`.
 *
 * @remarks
 * - Supports rendering paragraphs, headings (h1-h3), links, lists (ordered and unordered), and list items.
 * - Supports text modifiers such as bold and italic.
 * - Uses the `BlocksRenderer` component for rendering the content structure.
 * - Applies consistent styling using utility classes and the optional `className` prop.
 */
export const RichTextRenderer: React.FC<RichTextRendererProps> = ({ content, className }) => {
  return (
  content === undefined || content === null ? null :
  <div id="blocks-editor" className={cn("w-full")}>
    <BlocksRenderer
      content={content}
      blocks={{
        paragraph: ({ children }) => <p className={cn("!font-normal font-texts  text-base leading-6 mobile:leading-3",className)}>{children}</p>,
        heading: ({ children, level }) => {
          const HeadingTag = `h${level || 2}` as React.ElementType
          const classSize = level === 1 ? '!text-6xl leading-10 mobile:leading-3' : level === 2 ? '!text-3xl leading-10 mobile:leading-3' : level === 3 ? '!text-2xl leading-10 mobile:leading-3' : 'text-lg'
          const classes = `font-headings ${classSize} font-bold`
          return <HeadingTag className={cn(classes, className)} >{children}</HeadingTag>
        },
        link: ({ children, url }) => (
          <Link href={url} className={cn('underline font-texts font-normal text-base leading-10 mobile:leading-3',className)} target="_blank" rel="noopener noreferrer">
            {children}
          </Link>
        ),
        "list-item": ({ children }) => <li className={cn('font-texts font-normal text-base',className)}>{children}</li>,
        list: ({ children, format }) => {
          const ListTag = format === 'ordered' ? 'ol' : 'ul'
          const classBullet = format === 'ordered' ? 'pl-8 list-decimal list-inside' : 'pl-8 list-disc list-inside mb-5'
          return <ListTag className={cn(classBullet,className)}>{children}</ListTag>
        },
        image: ({image}) => (
          <figure className="my-6 text-center">
            <Image
              src={image?.url}
              alt={image?.alternativeText || ''}
              width={image?.width || 800}
              height={image?.height || 500}
              className="my-4 w-full h-auto"
            />
            {image?.caption && <figcaption className="text-sm text-gray-500 mt-2">{image?.caption}</figcaption>}
          </figure>
        ),
      }}
      modifiers={{
        bold: ({ children }) => <b className={cn("leading-10 mobile:leading-3 font-texts ",className)}>{children}</b>,
        italic: ({ children }) => <span className={cn("italic font-texts font-normal  ", className)}>{children}</span>,
      }} />
    </div>
  )
}
